/**
 *
 * @module PageArea/Store
 * @requires npm:vue
 * @requires npm:vuex
 * @requires npm:lodash
 * @requires API/locations
 * @requires npm:moment
 * @requires Utils/notify
 * */

import Vue from 'vue'
import Vuex from 'vuex'
import { cloneDeep, isEqual } from 'lodash'
import * as locationService from 'API/locations'
import moment from 'moment'
import { notifyError } from 'Utils/notify'
import { UrlHelper } from 'Utils/UrlHelper'

Vue.use(Vuex)

/**
 * День для интервала
 * @typedef {Object}
 * @name Day
 * @property {String} label - метка дня (пн-вс)
 * @property {Boolean} isEnabled - день активирован
 * @property {Number|Null} limit - ограничение на день
 * @memberOf module:PageArea
 * */

/**
 * Фабрика по созданию пустых объектов [День]{@link module:PageArea.Day}
 * @function
 * @param {String} label - метка дня (пн-вс)
 * @returns {module:PageArea.Day}
 * */
const emptyDay = (label = '') => ({
  label,
  isEnabled: false,
  limit: null
})

/**
 * Интервал
 * @typedef {Object}
 * @name Interval
 * @memberOf module:PageArea
 * @property {String} id - ID
 * @property {String} deliveryOptionId - ID опции доставки
 * @property {Boolean} isEnabled - интервал активирован
 * @property {String} startTime - время начала
 * @property {String} endTime - время окончания
 * @property {Number|Null} blockInterval - ограничение для интервала
 * @property {Array.<module:PageArea.Day>} days - дни интервала (неделя пн-вс)
 * */

/**
 * Пустой объект интервала
 * @type {module:PageArea.Interval}
 * */
const emptyInterval = {
  id: null,
  deliveryOptionId: null,
  isEnabled: false,
  startTime: '',
  endTime: '',
  blockInterval: null,
  days: [
    emptyDay('Пн'),
    emptyDay('Вт'),
    emptyDay('Ср'),
    emptyDay('Чт'),
    emptyDay('Пт'),
    emptyDay('Сб'),
    emptyDay('Вс')
  ],
  sum_from: 0,
  sum_to: 0,
  cost: 0
}

/**
 * Цена доставки
 * @typedef {Object}
 * @name DeliveryCost
 * @memberOf module:PageArea
 * @property {Number} orderCostFrom - сумма заказа от
 * @property {Number} orderCostTo - сумма заказа до
 * @property {Number} deliveryCost - стоимость доставки
 * */

/**
 * Фигура зоны доставки
 * @typedef {Object}
 * @name Shape
 * @memberOf module:PageArea
 * @property {String} id - ID фигуры
 * @property {Object} polygon - фигура(геометрия, опции)
 * @property {Number[][]} polygon.geometry - фигура, Контуры[Контур][Точка]
 * */

/**
 * Пустой объект фигуры
 * @type {module:PageArea.Shape}
 * */
const emptyShape = {
  polygon: {
    geometry: []
  },
  id: null
}

/**
 * @const {String} - мутация SET_CURRENT_AREA
 * */
const SET_CURRENT_AREA = 'SET_CURRENT_AREA'
/**
 * @const {String} - мутация UPDATE_CURRENT_AREA
 * */
const UPDATE_CURRENT_AREA = 'UPDATE_CURRENT_AREA'
/**
 * @const {String} - мутация SET_AREAS
 * */
const SET_AREAS = 'SET_AREAS'
/**
 * @const {String} - мутация ADD_AREA
 * */
const ADD_AREA = 'ADD_AREA'
/**
 * @const {String} - мутация UPDATE_AREA
 * */
const UPDATE_AREA = 'UPDATE_AREA'
/**
 * @const {String} - мутация SET_INTERVALS
 * */
const SET_INTERVALS = 'SET_INTERVALS'
/**
 * @const {String} - мутация CLEAR
 * */
const CLEAR = 'CLEAR'
/**
 * @const {String} - мутация SET_SHOP
 * */
const SET_SHOP = 'SET_SHOP'
/**
 * @const {String} - мутация SET_DELIVERY_OPTION_ID
 * */
const SET_DELIVERY_OPTION_ID = 'SET_DELIVERY_OPTION_ID'
/**
 * @const {String} - мутация ADD_SHAPE
 * */
const ADD_SHAPE = 'ADD_SHAPE'
/**
 * @const {String} - мутация UPDATE_SHAPE
 * */
const UPDATE_SHAPE = 'UPDATE_SHAPE'
/**
 * @const {String} - мутация DELETE_SHAPE
 * */
const DELETE_SHAPE = 'DELETE_SHAPE'

moment.locale('en')
/**
 * @const {String} - имена дней недели на английском
 * */
const EN_WEEKDAYS = moment.weekdays(true)
  .map(el => el.toLowerCase())

moment.locale('ru')
/**
 * @const {String} - имена дней недели на русском
 * */
const RU_WEEKDAYS = Array(7)
  .fill(null)
  .map((el, index) => moment()
    .weekday(index)
    .format('dd'))

/**
 * Зона доставки
 * @typedef {Object}
 * @name Area
 * @memberOf module:PageArea
 * @property {String} name - Имя зоны доставки
 * @property {String} description - описание зоны доставки
 * @property {String} id - ID
 * @property {Array.<module:PageArea.Shape>} shapes - фигуры/области зоны доставки
 * */

/**
 * Магазин
 * @typedef {Object}
 * @name Shop
 * @memberOf module:PageArea
 * @property {String} id - ID
 * @property {String} address - адрес
 * @property {String} city - город
 * @property {String} street - улица
 * @property {String} house - дом
 * @property {Array.<Number>} coordinates - координаты
 * */

/**
 * Фабрика возвращающая дефолтное состояние хранилища
 * @function
 * @returns {Object}
 * */
const defaultState = () => ({
  /**
   * Интервалы
   * @name state/intervals
   * @type {Array.<module:PageArea.Interval>}
   * */
  intervals: [cloneDeep(emptyInterval)],
  /**
   * ID опции доставки, к ней привязаны цены и интервалы, а опция привязана к зоне(area)
   * @name state/deliveryOptionId
   * @type {String|Null}
   * */
  deliveryOptionId: null,
  /**
   * Редактируемая/создаваемая зона
   * @name state/currentArea
   * @type {module:PageArea.Area}
   * */
  currentArea: {
    name: null,
    description: null,
    id: null,
    shapes: [cloneDeep(emptyShape)]
  },
  /**
   * Другие зоны магазина
   * @name state/areas
   * @type {Array.<module:PageArea.Area>}
   * */
  areas: [],
  /**
   * Информация о магазине
   * @name state/shop
   * @type {module:PageArea.Shop}
   * */
  shop: {
    id: null,
    coordinates: null,
    address: null,
    city: null,
    street: null,
    house: null
  }
})

const store = new Vuex.Store({
  state: defaultState(),
  getters: {
    /**
     * Получить пустой объект интервала
     * @name getters/getEmptyInterval
     * @method
     * @returns {module:PageArea.Interval}
     * */
    getEmptyInterval: () => () => cloneDeep(emptyInterval),

    serializeArea: (state, getters) => (
      /**
       * Сериализовать зону доставки
       * @name getters/serializeArea
       * @method
       * @param {module:PageArea.Area} data
       * @returns {Object}
       * */
      (data) => ({
        area_name: data.name,
        area_description: data.description,
        area_is_hidden: false,
        area_type: 'area',
        shop_id: state.shop.id,
        geometry: data.shapes.map(shape => getters.serializeGeometry(shape.polygon.geometry))
      })
    ),

    serializeAreaForUpdate: () => (
      /**
       * Сериализовать зону доставки для обновления
       * @name getters/serializeAreaForUpdate
       * @method
       * @param {module:PageArea.Area} data
       * @returns {Object}
       * */
      (data) => ({
        id: data.id,
        name: data.name,
        description: data.description,
        is_hidden: false,
        type: 'area'
      })
    ),

    serializeDeliveryOption: (state, getters) => (
      /**
       * Сериализовать опцию доставки
       * @name getters/serializeDeliveryOption
       * @method
       * @param {Object} data
       * @returns {Object}
       * */
      (data) => ({
        delivery_option_id: data.deliveryOptionId,
        area_id: data.areaId,
        shop_id: data.shopId,
        intervals: data.intervals?.map(el => getters.serializeInterval(el))
      })
    ),

    serializeInterval: (state, getters) => (
      /**
       * Сериализовать интервал
       * @name getters/serializeInterval
       * @method
       * @param {module:PageArea.Interval} data
       * @returns {Object}
       * */
      (data) => ({
        id: data.id,
        delivery_option_id: data.deliveryOptionId,
        is_active: data.isEnabled,
        available_from: data.startTime || null,
        available_to: data.endTime || null,
        blocking_for: +data.blockInterval || null,
        daily_restrictions: data.days.map(el => getters.serializeDay(el)),
        sum_from: +data.orderCostFrom,
        sum_to: +data.orderCostTo,
        cost: +data.deliveryCost
      })
    ),

    serializeDay: (state, getters) => (
      /**
       * Сериализовать день
       * @name getters/serializeDay
       * @method
       * @param {module:PageArea.Day} data
       * @returns {Object}
       * */
      (data) => ({
        code: getters.serializeDayLabel(data.label),
        is_active: data.isEnabled,
        max_count_of_orders: +data.limit || null
      })
    ),

    serializeDayLabel: () => (
      /**
       * Сериализовать метку дня
       * @name getters/serializeDayLabel
       * @method
       * @param {String} data
       * @returns {String}
       * */
      (data) => {
        const index = RU_WEEKDAYS.findIndex(el => el === data.toLowerCase())
        return EN_WEEKDAYS[index]
      }
    ),

    serializeShape: (state, getters) => (
      /**
       * Сериализовать фигуру доставки
       * @name getters/serializeShape
       * @method
       * @param {module:PageArea.Shape} data
       * @returns {Object}
       * */
      (data) => ({
        geometry: getters.serializeGeometry(data.polygon.geometry),
        area_id: state.currentArea.id,
        shape_id: data.id
      })
    ),

    serializeGeometry: () => (
      /**
       * Сериализовать геометрию
       * @name getters/serializeGeometry
       * @method
       * @param {Object} data
       * @returns {Object}
       * */
      (data) => data[0].map(point => ({
        latitude: point[0],
        longitude: point[1]
      }))
    ),

    deserializeDeliveryOption: (state, getters) => (
      /**
       * Десериализовать опцию доставки
       * @name getters/deserializeDeliveryOption
       * @method
       * @param {Object} data
       * @returns {Object}
       * */
      (data) => ({
        deliveryOptionId: data.id,
        areaId: data.area_id,
        shopId: UrlHelper.GetParameter("shopId"),
        intervals: data.interval?.map(el => getters.deserializeInterval(el)) ?? []
      })
    ),

    deserializeArea: (state, getters) => (
      /**
       * Десериализовать зону доставки
       * @name getters/deserializeDeliveryOption
       * @method
       * @param {Object} data
       * @returns {module:PageArea.Area}
       * */
      (data) => ({
        id: data.id,
        name: data.name,
        description: data.description,
        shapes: data.shapes?.map(el => getters.deserializeShape(el)) ?? []
      })
    ),

    deserializeInterval: (state, getters) => (
      /**
       * Десериализовать интервал
       * @name getters/deserializeInterval
       * @method
       * @param {Object} data
       * @returns {module:PageArea.Interval}
       * */
      (data) => ({
        id: data.id,
        deliveryOptionId: data.delivery_option_id,
        isEnabled: data.is_active,
        startTime: data.available_from,
        endTime: data.available_to,
        blockInterval: data.blocking_for,
        days: data.daily_restrictions?.map(el => getters.deserializeDay(el)) ?? [],
        orderCostFrom: data.sum_from,
        orderCostTo: data.sum_to,
        deliveryCost: data.cost
      })
    ),

    deserializeDay: (state, getters) => (
      /**
       * Десериализовать день
       * @name getters/deserializeDay
       * @method
       * @param {Object} data
       * @returns {module:PageArea.Day}
       * */
      (data) => ({
        label: getters.deserializeDayLabel(data.code),
        isEnabled: data.is_active,
        limit: data.max_count_of_orders
      })
    ),

    deserializeDayLabel: () => (
      /**
       * Десериализовать метку дня
       * @name getters/deserializeDayLabel
       * @method
       * @param {String} data
       * @returns {String}
       * */
      (data) => {
        const index = EN_WEEKDAYS.findIndex(el => el === data.toLowerCase())
        return RU_WEEKDAYS[index]
      }
    ),

    deserializeShape: () => (
      /**
       * Десериализовать фигуру
       * @name getters/deserializeShape
       * @method
       * @param {Object} data
       * @returns {module:PageArea.Shape}
       * */
      (data) => {
        const contour = data.polygon.geometry
        return ({
          polygon: {
          // Массив контуров
            geometry: [
            // Контур
              contour.map(
              // Массив точек контура
                point => [point.x, point.y]
              )
            ]
          },
          id: data.shape_id
        })
      }
    ),

    deserializeShop: () => (
      /**
       * Десериализовать магазин
       * @name getters/deserializeShop
       * @method
       * @param {Object} data
       * @returns {module:PageArea.Shop}
       * */
      (data) => ({
        id: data.id,
        address: data.address.addr,
        city: data.address.city,
        street: data.address.street,
        house: data.address.house,
        coordinates: Object.values(data.address.coordinates)
      })
    ),

    getAreaById: (state) => (
      /**
       * Получить зону доставки по ID
       * @name getters/getAreaById
       * @method
       * @param {String} id
       * @returns {module:PageArea.Area}
       * */
      (id) => state.areas.find(el => el.id === id)
    )
  },
  actions: {
    /**
     * Получить опции зоны по ID зоны
     * @async
     * @function
     * @name actions/getAreaOptionsById
     * @param {Object} context
     * @param {String} id
     * @param {String} shopId
     * @returns {Promise.<void>}
     * */
    async getAreaOptionsById({
      commit, state, getters, dispatch
    }, id, shopId = UrlHelper.GetParameter("shopId")) {
      try {
        const { data } = await locationService.getAreaOptions(id, shopId)
        if (data.length) {
          data.forEach(option => {
            const deserializedOption = getters.deserializeDeliveryOption(option)

            commit(
              SET_INTERVALS,
              deserializedOption.intervals.length
                ? deserializedOption.intervals
                : [cloneDeep(emptyInterval)]
            )
            commit(SET_SHOP, {
              id: deserializedOption.shopId
            })
            commit(SET_DELIVERY_OPTION_ID, deserializedOption.deliveryOptionId)
          })
        }

        commit(UPDATE_CURRENT_AREA, {
          id
        })
        await dispatch('getAreaShapes', id)
      } catch (e) {
        console.error(e)
      }
    },

    /**
     * Получить фигуры зоны по ID зоны
     * @async
     * @function
     * @name actions/getAreaShapes
     * @param {Object} context
     * @param {String} id
     * @returns {Promise.<void>}
     * */
    async getAreaShapes({ commit, getters, state }, id) {
      try {
        const { data } = await locationService.getAreaWithShapes(id)
        const deserializedArea = getters.deserializeArea(data.areas)

        if (id === state.currentArea.id) {
          commit(UPDATE_CURRENT_AREA, deserializedArea)
          return
        }

        if (getters.getAreaById(deserializedArea.id)) {
          commit(UPDATE_AREA, deserializedArea)
        } else {
          commit(ADD_AREA, deserializedArea)
        }
      } catch (e) {
        console.error(e)
      }
    },

    /**
     * Получить зоны по ID магазина
     * @async
     * @function
     * @name actions/getShopAreas
     * @param {Object} context
     * @param {String} shopId
     * @returns {Promise.<void>}
     * */
    async getShopAreas({
      getters, state, dispatch, commit
    }, shopId = UrlHelper.GetParameter("shopId")) {
      try {
        const { data } = await locationService.getShopAreas(shopId)
        const areas = data.list?.[0]?.areas.map(el => getters.deserializeArea(el)) || []
        let shop = { id: shopId }
        if (data.list?.[0]?.shop) shop = getters.deserializeShop(data.list?.[0]?.shop)
        commit(SET_SHOP, shop)

        await Promise.all(
          areas.filter(area => area.id !== state.currentArea.id)
            .map(area => dispatch('getAreaShapes', area.id))
        )
      } catch (e) {
        console.error(e)
      }
    },

    /**
     * Создать зону, интервалы и стоимость
     * @async
     * @function
     * @name actions/save
     * @param {Object} context
     * @param {Object} payload
     * @param {Object} payload.area - зона
     * @param {Object} payload.intervals - интервалы
     * @returns {Promise.<void>}
     * */
    async create({ dispatch }, { area, intervals }) {
      try {
        await dispatch('createArea', area)
        await dispatch('createOption', area)
        await dispatch('setIntervals', intervals)
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Обновить зону, интервалы и стоимость
     * @async
     * @function
     * @name actions/update
     * @param {Object} context
     * @param {Object} payload
     * @param {Object} payload.area - зона
     * @param {Object} payload.intervals - интервалы
     * @returns {Promise.<void>}
     * */
    async update({ dispatch, state }, { area, intervals }) {
      try {
        const deletedShapes = state.currentArea.shapes.filter(
          curAreaShape => !area.shapes.find(
            chAreaShape => chAreaShape.id === curAreaShape.id
          )
        )
        const updatedShapes = area.shapes.filter(
          curAreaShape => state.currentArea.shapes.some(
            chAreaShape => (
              chAreaShape.id === curAreaShape.id
              && !isEqual(curAreaShape, chAreaShape)
            )
          )
        )
        const createdShapes = area.shapes.filter(chAreaShape => !chAreaShape.id)

        const shapesPromises = [
          ...createdShapes.map(shape => dispatch('createShape', shape)),
          ...updatedShapes.map(shape => dispatch('updateShape', shape)),
          ...deletedShapes.map(shape => dispatch('deleteShape', shape.id))
        ]
        await Promise.all(shapesPromises)
        await dispatch('updateArea', area)
        await dispatch('setIntervals', intervals)
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Создать опцию доставки
     * @async
     * @function
     * @name actions/createOption
     * @param {Object} context
     * @returns {Promise.<void>}
     * */
    async createOption({ getters, commit, state }) {
      try {
        const { data } = await locationService.createOption(
          getters.serializeDeliveryOption({
            areaId: state.currentArea.id,
            shopId: UrlHelper.GetParameter("shopId")
          })
        )
        commit(SET_DELIVERY_OPTION_ID, data.id)
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Создать зону доставки
     * @async
     * @function
     * @name actions/createArea
     * @param {Object} context
     * @param {module:PageArea.Area} payload
     * @returns {Promise.<void>}
     * */
    async createArea({ getters, commit }, payload) {
      try {
        const { data } = await locationService.createArea(getters.serializeArea(payload))
        commit(SET_CURRENT_AREA, getters.deserializeArea(data.area))
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },


    /**
     * Установить интервалы в опцию доставки
     * @async
     * @function
     * @name actions/setIntervals
     * @param {Object} context
     * @param {Array.<module:PageArea.Interval>} intervals
     * @returns {Promise.<void>}
     * */
    async setIntervals({ getters, state, commit }, intervals) {
      try {
        let shopId = UrlHelper.GetParameter("shopId")
        const serializedOption = getters.serializeDeliveryOption({
          shopId: shopId,
          areaId: state.currentArea.id,
          deliveryOptionId: state.deliveryOptionId,
          intervals
        })

        const { data } = await locationService.setOptionIntervals(serializedOption)

        const deserializedResponse = data.intervals.map(el => getters.deserializeInterval(el))
        commit(
          SET_INTERVALS,
          deserializedResponse.length
            ? deserializedResponse
            : [cloneDeep(emptyInterval)]
        )
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Обновить зону доставки
     * @async
     * @function
     * @name actions/updateArea
     * @param {Object} context
     * @param {module:PageArea.Area} payload
     * @returns {Promise.<void>}
     * */
    async updateArea({ getters, commit }, payload) {
      try {
        const { data } = await locationService.updateArea(getters.serializeAreaForUpdate(payload))
        commit(UPDATE_AREA, getters.deserializeArea(data))
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Создать фигуру
     * @async
     * @function
     * @name actions/createShape
     * @param {Object} context
     * @param {module:PageArea.Shape} shape
     * @returns {Promise.<void>}
     * */
    async createShape({ getters, commit }, shape) {
      try {
        const { data } = await locationService.createAreaShape(getters.serializeShape(shape))
        commit(ADD_SHAPE, getters.deserializeShape(data))
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Обновить фигуру
     * @async
     * @function
     * @name actions/updateShape
     * @param {Object} context
     * @param {module:PageArea.Shape} shape
     * @returns {Promise.<void>}
     * */
    async updateShape({ getters, commit }, shape) {
      try {
        const { data } = await locationService.updateAreaShape(getters.serializeShape(shape))
        commit(UPDATE_SHAPE, getters.deserializeShape(data))
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Удалить фигуру
     * @async
     * @function
     * @name actions/deleteShape
     * @param {Object} context
     * @param {String} shapeId
     * @returns {Promise.<void>}
     * */
    async deleteShape({ commit }, shapeId) {
      try {
        await locationService.deleteAreaShape(shapeId)
        commit(DELETE_SHAPE, shapeId)
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.method)
        throw err
      }
    },

    /**
     * Очистить хранилище
     * @function
     * @name actions/clearStore
     * @param {Object} context
     * */
    clearStore({ commit }) {
      commit(CLEAR)
    }
  },
  mutations: {
    /**
     * Установить другие зоны магазина
     * @function
     * @name mutations/SET_AREAS
     * @param {Object} state
     * @param {Array.<module:PageArea.Area>} payload
     * */
    [SET_AREAS](state, payload) {
      state.areas = payload
    },

    /**
     * Добавить зону в список других зон магазина
     * @function
     * @name mutations/ADD_AREA
     * @param {Object} state
     * @param {module:PageArea.Area} payload
     * */
    [ADD_AREA](state, payload) {
      state.areas.push(payload)
    },

    /**
     * Обновить зону в списке других зон магазина
     * @function
     * @name mutations/UPDATE_AREA
     * @param {Object} state
     * @param {module:PageArea.Area} payload
     * */
    [UPDATE_AREA](state, payload) {
      const index = state.areas.findIndex(el => payload.id === el.id)
      state.areas.splice(index, 1, payload)
    },

    /**
     * Установить текущую(редактируемую) зону
     * @function
     * @name mutations/SET_CURRENT_AREA
     * @param {Object} state
     * @param {module:PageArea.Area} payload
     * */
    [SET_CURRENT_AREA](state, payload) {
      state.currentArea = payload
    },

    /**
     * Обновить текущую(редактируемую) зону
     * @function
     * @name mutations/UPDATE_CURRENT_AREA
     * @param {Object} state
     * @param {Object} payload - патч
     * */
    [UPDATE_CURRENT_AREA](state, payload) {
      state.currentArea = {
        ...state.currentArea,
        ...payload
      }
    },

    /**
     * Сбросить хранилище
     * @function
     * @name mutations/CLEAR
     * @param {Object} state
     * */
    [CLEAR](state) {
      Object.entries(defaultState())
        .forEach(([key, value]) => {
          Vue.set(state, key, value)
        })
    },

    /**
     * Установить интервалы
     * @function
     * @name mutations/SET_INTERVALS
     * @param {Object} state
     * @param {Array.<module:PageArea.Interval>} payload
     * */
    [SET_INTERVALS](state, payload) {
      state.intervals = payload
    },

    /**
     * Установить магазин
     * @function
     * @name mutations/SET_SHOP
     * @param {Object} state
     * @param {module:PageArea.Shop} payload
     * */
    [SET_SHOP](state, payload) {
      state.shop = payload
    },

    /**
     * Установить ID опции доставки
     * @function
     * @name mutations/SET_DELIVERY_OPTION_ID
     * @param {Object} state
     * @param {String} payload - ID
     * */
    [SET_DELIVERY_OPTION_ID](state, payload) {
      state.deliveryOptionId = payload
    },

    /**
     * Добавить фигуру в редактируемую зону
     * @function
     * @name mutations/ADD_SHAPE
     * @param {Object} state
     * @param {module:PageArea.Shape} payload
     * */
    [ADD_SHAPE](state, payload) {
      state.currentArea.shapes.push(payload)
    },

    /**
     * Обновить фигуру в редактируемой зоне
     * @function
     * @name mutations/UPDATE_SHAPE
     * @param {Object} state
     * @param {module:PageArea.Shape} payload
     * */
    [UPDATE_SHAPE](state, payload) {
      const index = state.currentArea.shapes.findIndex(shape => shape.id === payload.id)
      state.currentArea.shapes.splice(index, 1, payload)
    },

    /**
     * Удалить фигуру в редактируемой зоне
     * @function
     * @name mutations/UPDATE_SHAPE
     * @param {Object} state
     * @param {Number} index
     * */
    [DELETE_SHAPE](state, index) {
      state.currentArea.shapes.splice(index, 1)
    }
  }
})

export default store
